'use client';

import { useState } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, BarChart, Bar } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface Hypothesis {
  id: string;
  title: string;
  confidence: number;
  status: 'testing' | 'confirmed' | 'rejected' | 'pending';
  correlation: number;
  pValue: number;
}

interface NotebookCell {
  id: string;
  type: 'markdown' | 'code' | 'result';
  content: string;
  output?: string;
}

const HYPOTHESES: Hypothesis[] = [
  { id: 'h1', title: 'Back-to-back games reduce xG by 12-18%', confidence: 87, status: 'confirmed', correlation: 0.74, pValue: 0.003 },
  { id: 'h2', title: 'Travel > 2000km increases fatigue score by 23%', confidence: 82, status: 'confirmed', correlation: 0.68, pValue: 0.008 },
  { id: 'h3', title: 'Goalie starts on 3+ days rest improve SV% by 2.1%', confidence: 91, status: 'confirmed', correlation: 0.81, pValue: 0.001 },
  { id: 'h4', title: 'Home crowd noise correlates with penalty differential', confidence: 61, status: 'testing', correlation: 0.42, pValue: 0.089 },
  { id: 'h5', title: 'Line chemistry index predicts PP% within 3.2%', confidence: 78, status: 'testing', correlation: 0.63, pValue: 0.021 },
  { id: 'h6', title: 'Emotional volatility spikes before rivalry games', confidence: 44, status: 'rejected', correlation: 0.28, pValue: 0.214 },
];

const NOTEBOOK_CELLS: NotebookCell[] = [
  { id: 'c1', type: 'markdown', content: '## Analyse: Impact B2B sur xG\n\nObjectif: Valider la corrélation entre matchs consécutifs et performance offensive.' },
  { id: 'c2', type: 'code', content: 'model = PoissonRegressor(alpha=0.1)\nX = df[["b2b_flag", "travel_km", "rest_days"]]\ny = df["xg_per_60"]\nmodel.fit(X_train, y_train)\nprint(f"R² = {model.score(X_test, y_test):.3f}")', output: 'R² = 0.742\nCoefficients: b2b=-0.183, travel=-0.0002, rest=+0.041' },
  { id: 'c3', type: 'result', content: 'Résultat: Corrélation significative (p=0.003). B2B réduit xG de 18.3% en moyenne.', output: 'HYPOTHESIS CONFIRMED ✓' },
];

const CORRELATION_DATA = Array.from({ length: 40 }, () => ({
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 20 + 5,
}));

const PARAM_SIMULATION = Array.from({ length: 20 }, (_, i) => ({
  alpha: (i * 0.05).toFixed(2),
  accuracy: 65 + Math.sin(i * 0.4) * 8 + Math.random() * 3,
  overfitting: Math.max(0, (i - 10) * 1.5 + Math.random() * 2),
}));

const MODEL_COMPARISON = [
  { name: 'Baseline', accuracy: 63.2, precision: 61.4, recall: 58.9, f1: 60.1 },
  { name: 'Poisson v3', accuracy: 68.4, precision: 67.1, recall: 65.8, f1: 66.4 },
  { name: 'xG Neural', accuracy: 73.8, precision: 72.4, recall: 71.2, f1: 71.8 },
  { name: 'Ensemble', accuracy: 76.3, precision: 75.1, recall: 74.6, f1: 74.8 },
  { name: 'Experimental', accuracy: 78.1, precision: 77.2, recall: 76.8, f1: 77.0 },
];

export default function ResearchLabClient() {
  const [activeTab, setActiveTab] = useState<'hypotheses' | 'notebook' | 'simulation' | 'comparison'>('hypotheses');
  const [selectedHypothesis, setSelectedHypothesis] = useState<Hypothesis | null>(null);
  const [simAlpha, setSimAlpha] = useState(0.5);

  const statusColors: Record<string, string> = {
    confirmed: 'text-green-400 bg-green-400/10 border-green-400/30',
    testing: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
    rejected: 'text-red-400 bg-red-400/10 border-red-400/30',
    pending: 'text-muted-foreground bg-border/30 border-border',
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="BeakerIcon" size={20} className="text-emerald-400" />
            <h1 className="text-xl font-bold text-foreground">Research Lab</h1>
            <span className="px-2 py-0.5 text-2xs font-mono bg-emerald-400/10 text-emerald-400 border border-emerald-400/20 rounded">PHASE QUANT · ÉTAPE 5</span>
            <span className="px-2 py-0.5 text-2xs font-mono bg-background text-muted-foreground border border-border rounded">QUANT HEDGE FUND STYLE</span>
          </div>
          <p className="text-sm text-muted-foreground">Sandbox expérimental — modèles, hypothèses, simulations, corrélations</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-3 py-1.5 bg-emerald-400/10 border border-emerald-400/20 rounded text-xs text-emerald-400 font-mono">
            {HYPOTHESES.filter(h => h.status === 'confirmed').length} hypothèses confirmées
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {(['hypotheses', 'notebook', 'simulation', 'comparison'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === tab ? 'text-emerald-400 border-b-2 border-emerald-400' : 'text-muted-foreground hover:text-foreground'}`}>
            {tab === 'hypotheses' ? 'Hypothèses' : tab === 'notebook' ? 'Notebook Analytique' : tab === 'simulation' ? 'Simulation Paramètres' : 'Comparaison Modèles'}
          </button>
        ))}
      </div>

      {activeTab === 'hypotheses' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Hypothèses Générées Automatiquement</h3>
              <button className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-400/10 text-emerald-400 border border-emerald-400/20 rounded hover:bg-emerald-400/20 transition-colors">
                <Icon name="SparklesIcon" size={12} />
                Générer
              </button>
            </div>
            <div className="divide-y divide-border">
              {HYPOTHESES.map(h => (
                <div key={h.id} onClick={() => setSelectedHypothesis(h)}
                  className={`p-3 cursor-pointer hover:bg-background/50 transition-colors ${selectedHypothesis?.id === h.id ? 'bg-background/50' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs text-foreground flex-1">{h.title}</p>
                    <span className={`text-2xs px-1.5 py-0.5 rounded border flex-shrink-0 font-mono ${statusColors[h.status]}`}>
                      {h.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-2xs text-muted-foreground">Conf: <span className="text-foreground font-mono">{h.confidence}%</span></span>
                    <span className="text-2xs text-muted-foreground">r: <span className="text-foreground font-mono">{h.correlation}</span></span>
                    <span className="text-2xs text-muted-foreground">p: <span className={`font-mono ${h.pValue < 0.05 ? 'text-green-400' : 'text-red-400'}`}>{h.pValue}</span></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Analyse Corrélations</h3>
            <ResponsiveContainer width="100%" height={250}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="x" name="Variable X" tick={{ fill: '#64748b', fontSize: 10 }} label={{ value: 'Fatigue Index', fill: '#64748b', fontSize: 10, position: 'bottom' }} />
                <YAxis dataKey="y" name="Variable Y" tick={{ fill: '#64748b', fontSize: 10 }} label={{ value: 'xG/60', fill: '#64748b', fontSize: 10, angle: -90, position: 'insideLeft' }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
                <Scatter data={CORRELATION_DATA} fill="#10b981" fillOpacity={0.6} />
              </ScatterChart>
            </ResponsiveContainer>
            {selectedHypothesis && (
              <div className="mt-3 p-3 bg-background rounded border border-emerald-400/20">
                <div className="text-2xs text-emerald-400 font-mono mb-1">HYPOTHÈSE SÉLECTIONNÉE</div>
                <p className="text-xs text-foreground">{selectedHypothesis.title}</p>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <div className="text-center"><div className="text-sm font-bold text-emerald-400 font-mono">{selectedHypothesis.confidence}%</div><div className="text-2xs text-muted-foreground">Confiance</div></div>
                  <div className="text-center"><div className="text-sm font-bold text-blue-400 font-mono">{selectedHypothesis.correlation}</div><div className="text-2xs text-muted-foreground">Corrélation</div></div>
                  <div className="text-center"><div className={`text-sm font-bold font-mono ${selectedHypothesis.pValue < 0.05 ? 'text-green-400' : 'text-red-400'}`}>{selectedHypothesis.pValue}</div><div className="text-2xs text-muted-foreground">p-value</div></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'notebook' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Notebook Analytique — Impact B2B sur xG</h3>
            <div className="flex gap-2">
              <button className="px-3 py-1 text-xs bg-emerald-400/10 text-emerald-400 border border-emerald-400/20 rounded">▶ Exécuter</button>
              <button className="px-3 py-1 text-xs border border-border text-muted-foreground rounded">+ Cellule</button>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {NOTEBOOK_CELLS.map(cell => (
              <div key={cell.id} className={`rounded-lg border overflow-hidden ${cell.type === 'markdown' ? 'border-blue-400/20' : cell.type === 'code' ? 'border-yellow-400/20' : 'border-green-400/20'}`}>
                <div className={`px-3 py-1.5 text-2xs font-mono flex items-center gap-2 ${cell.type === 'markdown' ? 'bg-blue-400/5 text-blue-400' : cell.type === 'code' ? 'bg-yellow-400/5 text-yellow-400' : 'bg-green-400/5 text-green-400'}`}>
                  {cell.type === 'markdown' ? '📝 MARKDOWN' : cell.type === 'code' ? '⚡ CODE' : '📊 RÉSULTAT'}
                </div>
                <div className="p-3 bg-background">
                  <pre className="text-xs text-foreground font-mono whitespace-pre-wrap">{cell.content}</pre>
                  {cell.output && (
                    <div className="mt-2 pt-2 border-t border-border">
                      <pre className="text-xs text-green-400 font-mono">{cell.output}</pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'simulation' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Simulation Paramètre Alpha (Régularisation)</h3>
            <div className="mb-4">
              <label className="text-xs text-muted-foreground">Alpha: <span className="text-foreground font-mono">{simAlpha}</span></label>
              <input type="range" min="0" max="1" step="0.05" value={simAlpha} onChange={e => setSimAlpha(+e.target.value)}
                className="w-full mt-1 accent-emerald-400" />
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={PARAM_SIMULATION}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="alpha" tick={{ fill: '#64748b', fontSize: 9 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 9 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
                <Line type="monotone" dataKey="accuracy" stroke="#10b981" strokeWidth={2} dot={false} name="Précision %" />
                <Line type="monotone" dataKey="overfitting" stroke="#ef4444" strokeWidth={1.5} dot={false} name="Overfitting %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Résultats Simulation</h3>
            <div className="space-y-3">
              {[
                { param: 'Alpha optimal', value: '0.35', desc: 'Meilleur équilibre précision/overfitting' },
                { param: 'Précision max', value: '78.1%', desc: 'Atteint à alpha=0.35' },
                { param: 'Overfitting seuil', value: 'alpha > 0.65', desc: 'Zone à éviter' },
                { param: 'Stabilité', value: '0.1 - 0.5', desc: 'Plage de paramètres stables' },
              ].map(r => (
                <div key={r.param} className="p-3 bg-background rounded border border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">{r.param}</span>
                    <span className="text-sm font-bold text-emerald-400 font-mono">{r.value}</span>
                  </div>
                  <p className="text-2xs text-muted-foreground mt-0.5">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'comparison' && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Comparaison Expérimentale des Modèles</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={MODEL_COMPARISON}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} domain={[55, 82]} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11 }} />
              <Bar dataKey="accuracy" fill="#10b981" name="Précision %" radius={[4, 4, 0, 0]} />
              <Bar dataKey="precision" fill="#3b82f6" name="Précision Classe %" radius={[4, 4, 0, 0]} />
              <Bar dataKey="f1" fill="#a855f7" name="F1 Score %" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  {['Modèle', 'Accuracy', 'Precision', 'Recall', 'F1'].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-2xs font-medium text-muted-foreground uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MODEL_COMPARISON.map(m => (
                  <tr key={m.name} className={`border-b border-border/50 ${m.name === 'Experimental' ? 'bg-emerald-400/5' : ''}`}>
                    <td className="px-4 py-2 font-medium text-foreground">{m.name}</td>
                    <td className="px-4 py-2 font-mono text-green-400">{m.accuracy}%</td>
                    <td className="px-4 py-2 font-mono text-blue-400">{m.precision}%</td>
                    <td className="px-4 py-2 font-mono text-yellow-400">{m.recall}%</td>
                    <td className="px-4 py-2 font-mono text-purple-400">{m.f1}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
